import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Icon,
  Button,
} from '@mui/material';
import { AiOutlineArrowDown, AiOutlineArrowUp } from 'react-icons/ai';
import moment from 'moment';
import 'moment/locale/de';
import {
  GET_ALL_STORES_COUNT,
  GET_STORES_OFFSET,
} from '../queries/adminQueries';
import { useQuery } from '@apollo/client';

/**
 * CustomersView
 */
const CustomersView = (props: any) => {
  const [page, setPage] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortOrder, setSortOrder] = useState<string>('desc');

  // Die Anzahl der Einträge ermitteln.
  const {
    loading: countLoading,
    error: countError,
    data: countData,
    refetch: countRefetch,
  } = useQuery(GET_ALL_STORES_COUNT, {
    fetchPolicy: 'network-only',
  });

  // Die Einträge der einzelnen Offsets holen.
  const {
    loading: offsetLoading,
    error: offsetError,
    data: offsetData,
    fetchMore,
    refetch: offsetRefetch,
  } = useQuery(GET_STORES_OFFSET, {
    variables: {
      offset: page * pageSize,
      limit: pageSize,
      sortOrder: sortOrder,
    },
    fetchPolicy: 'network-only',
  });

  moment().locale('de');

  /**
   * Falls die Navigation vom Customer kommt.
   */
  useEffect(() => {
    if (Object.keys(props.childPagePropsFromParent).length !== 0) {
      setPage(props.childPagePropsFromParent.page);
      setPageSize(props.childPagePropsFromParent.pageSize);
      setSortOrder(props.childPagePropsFromParent.sortOrder);
    }
  }, [props.childPagePropsFromParent]);

  /**
   * Funktion um den Offset zu setzen.
   */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    fetchMore({
      variables: {
        offset: newPage * pageSize,
        limit: pageSize,
        sortOrder,
      },
    });
  };

  /**
   * Funktion um pageSize zu ändern.
   */
  const handleChangePageSize = (event) => {
    setPageSize(+event.target.value);
    setPage(0);
  };

  /**
   * Handle sortOrder
   */
  const handleSortOrder = () => {
    if (sortOrder === 'asc') {
      setSortOrder('desc');
    } else {
      setSortOrder('asc');
    }
  };

  // Beim laden der Daten nichts anzeigen.
  if (countLoading) {
    return null;
  }

  // Falls Fehler bei der Abfrage auftreten.
  if (countError) {
    console.log('Error! CustomersView: ' + countError.message);
  }

  // Beim laden der Daten nichts anzeigen.
  if (offsetLoading) {
    return null;
  }

  // Falls Fehler bei der Abfrage auftreten.
  if (offsetError) {
    console.log('Error! CustomersView: ' + offsetError.message);
  }

  // return
  return (
    <Container maxWidth={false}>
      <Box>
        <Typography component='h2' variant='h5' align='center' mt={2}>
          Geschäfte
        </Typography>
      </Box>
      <Box sx={{ marginTop: 2, width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 560 }}>
          <Table stickyHeader aria-label='sticky table' size='small'>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    '&:hover': {
                      backgroundColor: 'primary.main',
                    },
                    display: 'flex',
                    flexDirection: 'row',
                    justifyContent: 'flex-start',
                    alignItems: 'center',
                    width: 80,
                    maxWidth: 80,
                  }}
                  onClick={handleSortOrder}
                >
                  KndNr.
                  {sortOrder === 'asc' ? (
                    <Icon>
                      <AiOutlineArrowUp size='16' />
                    </Icon>
                  ) : (
                    <Icon>
                      <AiOutlineArrowDown size='16' />
                    </Icon>
                  )}
                </TableCell>
                <TableCell>Geschäftsname</TableCell>
                <TableCell sx={{ width: 90, maxWidth: 90 }}>Status</TableCell>
                <TableCell sx={{ width: 60, maxWidth: 60 }}>PLZ</TableCell>
                <TableCell>Ort</TableCell>
                <TableCell>Inhaber</TableCell>
                <TableCell>Straße</TableCell>
                <TableCell sx={{ width: 90, maxWidth: 90 }}>Erstellt</TableCell>
                <TableCell sx={{ width: 90, maxWidth: 90 }}>
                  Aktiviert
                </TableCell>
                <TableCell sx={{ width: 90, maxWidth: 90 }}>Rechnung</TableCell>
                <TableCell sx={{ width: 90, maxWidth: 90 }}>
                  Gekündigt
                </TableCell>
              </TableRow>
            </TableHead>
            {offsetData !== undefined && offsetData.store.length > 0 ? (
              <TableBody>
                {offsetData.store.map((store: any) => (
                  <TableRow
                    sx={{
                      backgroundColor:
                        store.storestatus === 'new'
                          ? '#FF3D00'
                          : store.storestatus === 'closed'
                          ? '#607D8B'
                          : undefined,
                    }}
                    key={store.id}
                    hover
                    onClick={() => {
                      props.sendChildPagePropsToParent({
                        id: store.id,
                        storename: store.storename,
                        status: store.storestatus,
                        zipcode: store.location.zipcode,
                        place: store.location.place,
                        storeowner: store.storeowner,
                        street: store.street,
                        created: store.created,
                        activated: store.activated,
                        invoice: store.invoice,
                        terminated: store.terminated,
                        page,
                        pageSize,
                        sortOrder,
                      });
                      props.sendChildPageToParent('customer');
                    }}
                  >
                    <TableCell sx={{ width: 100, maxWidth: 100 }}>
                      {store.id}
                    </TableCell>
                    <TableCell>{store.storename}</TableCell>
                    <TableCell>{store.storestatus}</TableCell>
                    <TableCell>{store.location.zipcode}</TableCell>
                    <TableCell>{store.location.place}</TableCell>
                    <TableCell>{store.storeowner}</TableCell>
                    <TableCell>{store.street}</TableCell>
                    <TableCell>{moment(store.created).format('L')}</TableCell>
                    <TableCell>
                      {store.activated !== null
                        ? moment(store.activated).format('L')
                        : '--/--'}
                    </TableCell>
                    <TableCell>
                      {store.invoice !== null
                        ? moment(store.invoice).format('L')
                        : '--/--'}
                    </TableCell>
                    <TableCell>
                      {store.terminated !== null
                        ? moment(store.terminated).format('L')
                        : '--/--'}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            ) : (
              <Typography variant='caption'>Keine vorhanden!</Typography>
            )}
          </Table>
        </TableContainer>
        <TablePagination
          labelRowsPerPage={'Zeilen:'}
          rowsPerPageOptions={[5, 10, 25]}
          component='div'
          count={countData.store_aggregate.aggregate.totalCount}
          rowsPerPage={pageSize}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangePageSize}
        />
      </Box>
      <Box
        mt={3}
        mb={4}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Button
          sx={{
            borderRadius: 30,
            width: '240px',
          }}
          type='submit'
          variant='contained'
          color='secondary'
          onClick={() => {
            countRefetch();
            offsetRefetch();
          }}
        >
          Aktualisieren
        </Button>
      </Box>
    </Container>
  );
};

export default CustomersView;
